import { useState } from 'react'
import {
  Stepper,
  Step,
  useToast,
  Stack,
  Skeleton,
  Inline,
  Text,
  Box,
  useResponsiveValue,
  useDialogController
} from '@machobear/design-system'
import { useEffect, useMemo } from 'react'
import { useHistory, useParams } from 'react-router'
import { assoc, pipe, pick } from 'ramda'
import { useTranslation } from 'react-i18next'

import { useAvailableCreditChecks, useGetPaymentMethod } from '@/hooks/use-block-and-pay'
import ExitConfirmationModal from '@/components/exit-confirmation-modal'
import { verticalDotsSkeleton } from './index.css'
import { FORM_STEP_NUMBER as FORM } from '../index'
import { useFormContext } from '../form-context'

const FormStepper = ({ data, isLoading }: { data: any; isLoading: boolean }) => {
  const { t } = useTranslation()
  const showToast = useToast()
  const { dirty } = useFormContext()
  const { trustScoreId, formType } = useParams<{ trustScoreId: string; formType: string }>()
  const history = useHistory()
  const { visible, showModal, closeModal } = useDialogController()
  const [clickedStep, setClickedStep] = useState<{ id?: string | number; stepNumber: number }>()
  const { setIsReadyToSubmit, temporaryPaymentMethod } = useFormContext()

  const handleOnStepClick = (step: { id?: string | number; stepNumber: number }) => {
    if (activeStep === 1 && !trustScoreId)
      return showToast({
        key: `credit-check-form-trust-score-standalone`,
        message: t('l.credit_check.standalone.form.warning', 'Please save your changes first.'),
        tone: 'critical'
      })

    setClickedStep(step)

    return dirty ? showModal() : handleStepChange(step)
  }

  const handleStepChange = (step: { id?: string | number; stepNumber: number }) => {
    history.push(FORM_STEP_NUMBER[step.stepNumber])
  }

  const handleModalConfirm = () => {
    handleStepChange(clickedStep!)
    closeModal()
  }

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })

  const { availableCreditChecks } = useAvailableCreditChecks()
  const { data: paymentMethod } = useGetPaymentMethod('paymentMethod')

  const hasAvailableCreditChecks = availableCreditChecks > 0
  const hasPaymentMethods =
    (paymentMethod && paymentMethod?.length !== 0) || temporaryPaymentMethod.length !== 0
  const showPayment = !hasAvailableCreditChecks

  const basic_info_required_fields = ['first_name', 'last_name', 'dob']
  const address_required_fields = [
    'street_number',
    'street_name',
    'city',
    'province',
    'postal_code'
  ]
  const trustworthiness_required_fields = [
    'id_provided',
    'linkedin_provided',
    'employment_provided',
    'income_provided'
  ]
  const rent_to_income_required_fields = ['household_income', 'listing_price']

  const isCreditCheckComplete = useMemo(() => {
    return (
      basic_info_required_fields.every(field => !!data?.tenant?.basic_info?.[field]) &&
      address_required_fields.every(field => !!data?.tenant?.address?.[field])
    )
  }, [data?.tenant])

  const isTenantScreeningComplete = useMemo(
    () =>
      trustworthiness_required_fields.every(field =>
        field === 'linkedin_provided'
          ? data?.trustworthiness?.[field] !== 'none'
          : data?.trustworthiness?.[field] !== null
      ) &&
      rent_to_income_required_fields.every(field =>
        field !== 'household_income' ? !!data?.rent_to_income?.[field] : true
      ),
    [data?.trustworthiness, data?.rent_to_income]
  )

  const isPaymentComplete = hasPaymentMethods || hasAvailableCreditChecks
  const isConfirmationComplete = false

  const formCompletion: { [key: string]: boolean } = {
    'credit-check': isCreditCheckComplete,
    'tenant-screening': isTenantScreeningComplete,
    payment: isPaymentComplete,
    confirmation: isConfirmationComplete
  }

  const divider = !showPayment ? 3 : 4

  const FORM_STEP_NUMBER: { [key: number]: string } = !showPayment
    ? pipe(pick(['1', '2']), assoc('3', FORM['4']))(FORM)
    : FORM

  const completionPercentage = Math.floor(
    Object.values(FORM_STEP_NUMBER).filter(field => formCompletion[field]).length * (100 / divider)
  )

  const step = Object.entries(FORM_STEP_NUMBER).find(([key, val]) => val === formType) || [
    '1',
    'credit-check'
  ]

  const activeStep = parseInt(step[0])

  useEffect(() => {
    setIsReadyToSubmit(isCreditCheckComplete && isTenantScreeningComplete && isPaymentComplete)
  }, [isCreditCheckComplete, isPaymentComplete, isTenantScreeningComplete])

  return (
    <Box
      paddingRight={{ mobile: 'gutter', tablet: 'large' }}
      paddingBottom={{ mobile: 'gutter', tablet: 'none' }}
      paddingX={{ mobile: 'gutter' }}
      width="full"
    >
      <ExitConfirmationModal
        visible={visible}
        closeModal={closeModal}
        handleModalConfirm={handleModalConfirm}
      />
      {isLoading && isMobile ? (
        <Skeleton.Shape height="50px" />
      ) : isLoading && !isMobile ? (
        <Stack align={'left'} space={'gutter'}>
          <Text weight="strong">
            {`0% ${t('l.standalone_trustscore.form_completion_percentage', 'Complete')}`}
          </Text>
          <Stack align={'left'} space={'xlarge'}>
            <Inline space="xsmall" alignY={'center'}>
              <Box position="relative">
                <Skeleton.Shape height="24px" width="24px" borderRadius="xlarge" />
                <Box className={verticalDotsSkeleton} />
              </Box>

              <Skeleton.Shape height="24px" width="134px" />
            </Inline>
            <Inline space="xsmall" alignY={'center'}>
              <Box position="relative">
                <Skeleton.Shape height="24px" width="24px" borderRadius="xlarge" />
                <Box className={verticalDotsSkeleton} />
              </Box>
              <Skeleton.Shape height="24px" width="134px" />
            </Inline>
            <Inline space="xsmall" alignY={'center'}>
              <Box position="relative">
                <Skeleton.Shape height="24px" width="24px" borderRadius="xlarge" />
                <Box className={verticalDotsSkeleton} />
              </Box>
              <Skeleton.Shape height="24px" width="134px" />
            </Inline>
            <Inline space="xsmall" alignY={'center'}>
              <Skeleton.Shape height="24px" width="24px" borderRadius="xlarge" />
              <Skeleton.Shape height="24px" width="134px" />
            </Inline>
          </Stack>
        </Stack>
      ) : (
        <Stepper
          label="Credit Check"
          mode="non-linear"
          activeStep={activeStep}
          onStepClick={handleOnStepClick}
          direction="vertical"
          percentage={Number(completionPercentage)}
          showProgress
          showProgressText={t('l.standalone_trustscore.form_completion_percentage', 'Complete')}
          stepCountLabel=""
          nextLabel=""
        >
          <Step
            id="credit_check"
            complete={formCompletion['credit-check']}
            label={t('r.profile.edit.credit_check.title', 'Credit Check')}
          />
          <Step
            id="tenant_screening"
            complete={formCompletion['tenant-screening']}
            label={t('l.credit_usage.sidemenu.tenant_screening.label', 'Tenant Screening')}
          />
          {showPayment ? (
            <Step
              id="payment"
              label={t('l.standalone_trustscore.form.basic_payment_title', 'Payment')}
              complete={formCompletion.payment}
            />
          ) : (
            <></>
          )}
          <Step
            id="confirmation"
            complete={formCompletion.confirmation}
            label={t('l.standalone_trustscore.form_confirmation', 'Confirmation')}
          />
        </Stepper>
      )}
    </Box>
  )
}

export default FormStepper
